import {z} from 'zod';
import {FEATURE_FLAGS} from '@packages/systems/feature-config/shared';
import {defineToolRegistryManifest} from '../../types/base-tool-types';

const toolName = 'list-site-branches' as const;

const argumentZod = z.object({
  statusGroup: z
    .enum(['inProgress', 'merged'])
    .optional()
    .describe(
      'Which status group to list. Omitting returns in-progress branches. Pass "merged" for merge history.'
    ),
  limit: z
    .number()
    .int()
    .min(1)
    .max(100)
    .optional()
    .describe('Page size (1-100, default 100).'),
  cursor: z
    .string()
    .min(1)
    .optional()
    .describe(
      'Opaque pagination cursor from a previous response. Pass the same statusGroup that response was for.'
    ),
});

export const listSiteBranchesManifest = defineToolRegistryManifest({
  id: toolName,
  prettyName: 'List site branches',
  family: 'site-branches',
  llmTool: {
    name: toolName,
    description:
      'Lists site-scope branches (Releases) for the current site. Each row id is the site-branch ID to pass as site_branch_id on later lifecycle tools. Returns branch metadata (name, display status, staging domain, pagination) and changeSummary: counts of touched pages, components, styles, and CMS items, plus contributors. changeSummary is left out when it could not be read, so do not treat a missing one as no changes. Use list-site-branch-changes for the changes themselves. Does not include page-scope branches. Use statusGroup=merged to page through merge history.',
    inputSchema: argumentZod,
  },
  type: 'server',
  featureGate: FEATURE_FLAGS.SITE_BRANCHES,
  requiredScopes: ['branches:read'],
  permission: [{resource: 'Site', action: 'read'}],
  confirmBeforeExecuting: false,
  isConcurrencySafe: () => true,
  isReadOnly: () => true,
});

export type ListSiteBranchesArgumentType = z.infer<typeof argumentZod>;
